import React, { useState, useEffect } from 'react';
import { X, Save, FileText } from 'lucide-react';
import { getLiabilityWaiverContent, updateLiabilityWaiverContent } from '../lib/liabilityWaivers';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'sonner';

interface CustomizeLiabilityWaiverModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_LIABILITY_WAIVER_CONTENT = `ASSUMPTION OF RISK AND RELEASE OF LIABILITY WAIVER

I understand that the services provided involve certain inherent risks, dangers, and hazards that could result in serious personal injury, property damage, or death. I voluntarily assume all risks associated with these services.

I acknowledge that:
1. I am participating in these services voluntarily
2. I understand the nature of the services being provided
3. I am in good physical condition and have no medical conditions that would prevent safe participation
4. I will follow all safety instructions and guidelines provided

RELEASE AND WAIVER:
I hereby release, waive, discharge, and covenant not to sue the service provider, its employees, agents, and representatives from any and all liability, claims, demands, actions, and causes of action whatsoever arising out of or related to any loss, damage, or injury that may be sustained while participating in these services.

INDEMNIFICATION:
I agree to indemnify and hold harmless the service provider from any loss, liability, damage, or costs they may incur arising out of or related to my participation in these services.

This waiver shall be binding upon my heirs, executors, administrators, and assigns.

By signing below, I acknowledge that I have read this waiver in its entirety, understand its contents, and agree to be bound by its terms.`;

export default function CustomizeLiabilityWaiverModal({ isOpen, onClose }: CustomizeLiabilityWaiverModalProps) {
  const { selectedBusiness } = useBusiness();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalContent, setOriginalContent] = useState('');

  // Load existing liability waiver content
  const loadContent = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoading(true);
    try {
      const existingContent = await getLiabilityWaiverContent(selectedBusiness.id);
      const finalContent = existingContent || DEFAULT_LIABILITY_WAIVER_CONTENT;
      
      setContent(finalContent);
      setOriginalContent(finalContent);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading liability waiver content:', error);
      toast.error('Failed to load liability waiver content');
      setContent(DEFAULT_LIABILITY_WAIVER_CONTENT);
      setOriginalContent(DEFAULT_LIABILITY_WAIVER_CONTENT);
    } finally {
      setLoading(false);
    }
  };

  // Save liability waiver content
  const handleSave = async () => {
    if (!selectedBusiness?.id || !content.trim()) {
      toast.error('Please enter liability waiver content');
      return;
    }

    setSaving(true);
    try {
      const success = await updateLiabilityWaiverContent(selectedBusiness.id, content.trim());
      
      if (success) {
        toast.success('Liability waiver content saved successfully');
        setOriginalContent(content);
        setHasChanges(false);
      } else {
        toast.error('Failed to save liability waiver content');
      }
    } catch (error) {
      console.error('Error saving liability waiver content:', error);
      toast.error('Failed to save liability waiver content');
    } finally {
      setSaving(false);
    }
  };

  // Handle content change
  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasChanges(newContent !== originalContent);
  };

  // Reset to default content
  const handleResetToDefault = () => {
    if (confirm('Are you sure you want to reset to the default liability waiver content? Your current changes will be lost.')) {
      setContent(DEFAULT_LIABILITY_WAIVER_CONTENT);
      setHasChanges(DEFAULT_LIABILITY_WAIVER_CONTENT !== originalContent);
    }
  };

  // Handle close with unsaved changes warning
  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close without saving?')) {
        setContent(originalContent);
        setHasChanges(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadContent();
    }
  }, [isOpen, selectedBusiness?.id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 mr-2" />
              Customize Liability Waiver
            </h2>
            <p className="text-gray-600 mt-1">Edit the liability waiver content that customers will see and sign</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading liability waiver content...</span>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Editing Guidelines</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Write your liability waiver in clear, simple language</li>
                  <li>• Include specific risks related to your services</li>
                  <li>• Consider consulting with a legal professional for your specific business needs</li>
                  <li>• The waiver will be displayed exactly as written below</li>
                </ul>
              </div>

              {/* Content Editor */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Liability Waiver Content
                  </label>
                  <button
                    onClick={handleResetToDefault}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Reset to Default
                  </button>
                </div>
                <textarea
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  className="w-full h-96 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
                  placeholder="Enter your liability waiver content here..."
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    {content.length} characters
                  </p>
                  {hasChanges && (
                    <p className="text-xs text-amber-600 font-medium">
                      • Unsaved changes
                    </p>
                  )}
                </div>
              </div>

              {/* Legal Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-amber-900 mb-2">⚖️ Legal Disclaimer</h3>
                <p className="text-sm text-amber-800">
                  This tool is provided for convenience only. The effectiveness of any liability waiver depends on 
                  local laws and specific circumstances. We strongly recommend consulting with a qualified attorney 
                  to ensure your waiver meets legal requirements for your jurisdiction and business type.
                </p>
              </div>

              {/* Preview */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Preview</h3>
                <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {content || 'No content entered...'}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This is how your liability waiver will appear to customers
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 font-medium">
                You have unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges || !content.trim()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 